/**
 * What a pricebook SERVICE and the parts it carries are worth together.
 *
 * How the two combine is the service's pricing strategy, and there are exactly
 * two answers. TIME & MATERIALS sums them: the labor and the parts are what
 * builds the price, so what the customer pays is `basePrice + Σ(part × qty)`.
 * FLAT PRICE does not: the operator has named the price of the outcome, and the
 * parts are what delivering it costs — a $2,000 flat service carrying $1,555 of
 * parts sells for $2,000, not $3,555. Pass `partsAreCostOnly` for that path.
 *
 * A BUNDLE is the third parent and neither answer fits it. Its price is what it
 * charges — typed by the operator (`FLAT`) or summed from its members
 * (`COMPONENT_SUM`) — and its members are what is inside that price rather than
 * anything charged beside it. Pass `isBundle`, and `bundleTotal` is the bundle's
 * own price.
 *
 * The pricebook used to state only the two halves — a "listed price" and a
 * parts subtotal — and never how they combine, so on the summing path the one
 * number being sold appeared nowhere on the surface that authors it.
 *
 * The arithmetic therefore is not redone here. `bundleEconomics` already owns
 * it for the estimate/job/invoice side, and a second implementation is exactly
 * how the two surfaces would drift apart again. This module only translates the
 * pricebook's shape (components + a resolved item cache) into the line shape
 * that helper reads.
 *
 * The one mapping worth stating: a pricebook component marked `isOptional` is
 * offered, not sold — it lands on the estimate deselected. That is the same
 * thing `isSelected: false` means to `bundleEconomics`, so an optional part
 * contributes to neither the parts subtotal nor the bundle total.
 */
import { chargesRevenue } from '@/lib/sales/line-economics';
import { bundleEconomics } from '@/lib/sales/service-bundles';
import type { ComponentGroup, ComponentGroupableLine } from '@/lib/sales/service-components';
import {
  serviceCardRevenue,
  serviceCost,
  type ServiceCost,
  type ServiceCostSource,
  type ServicePartCostSource,
} from '@/lib/sales/service-cost';
import { partPricingTreatmentForService, resolveMarkupPercent } from '@/lib/sales/service-rollup';
import { isFlatPricePricebookService } from './pricebook-helpers';
import type {
  LaborRateProfile,
  MarkupProfile,
  PricebookItem,
  ServiceComponent,
  ServiceLaborLine,
} from '@/types/pricebook';

export type PricebookComponentItemCacheItem = Partial<PricebookItem> & {
  id: string;
  name?: string;
  basePrice?: number | null;
  costBasis?: number | null;
};

export type PricebookComponentItemCache = Record<string, PricebookComponentItemCacheItem>;

/** Money, to the cent — a headline price must not carry float dust. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

export function getComponentItemPrice(item?: PricebookComponentItemCacheItem) {
  return Number(item?.price ?? item?.basePrice ?? 0);
}

export function getComponentItemCost(item?: PricebookComponentItemCacheItem) {
  const value = item?.cost ?? item?.costBasis;
  return value == null ? undefined : Number(value);
}

/** One material or equipment row a SERVICE carries, as the catalog resolves it. */
export type ServiceOwnPart = ServicePartCostSource;

/**
 * A member SERVICE's cost per unit. For a flat service, `costBasis` is the
 * operator-entered Other cost; for a dynamic service it is an already-derived
 * storage field and must not be added again.
 *
 * A service costs the TIME it takes plus what the parts it consumes cost, plus
 * explicit flat-service overhead. Its `costBasis` is not the whole answer: a
 * dynamically-priced service stores its composed result there, while a flat
 * service stores only Other cost there. Reading the column alone therefore
 * reports a $250 repair as free or omits its labor and parts — exactly what the
 * bundle contents table used to do for its service members.
 *
 * Labor and parts remain `serviceCost`'s arithmetic. This adapter adds the one
 * pricebook-only input that helper deliberately cannot accept: Other cost for
 * a flat service. Dynamic services exclude the stored column so their already
 * composed figure is never counted twice.
 *
 * All that is decided here is WHICH labour rows to hand it, which is a pricebook
 * question rather than a costing one. The rows are read from the side the
 * service is actually priced on and never from the other: switching a service
 * between the two strategies leaves the abandoned set in place, so reading both
 * would cost hours the operator stopped charging for. A service with no rows on
 * its own side falls through to the reduced pair — hours x hourly cost — which
 * is what a service typed straight in without rows behind it carries.
 *
 * Both sides go over as `laborLines`, INCLUDING a dynamic service's, and that is
 * deliberate. `serviceCost`'s `dynamicPricingConfig` slot is for a MATERIALIZED
 * service's snapshot, whose rows carry `resolvedTiers` — the rates frozen onto
 * the document when it was sold. A pricebook item has no snapshot: its dynamic
 * rows are the live configuration being authored, naming a rate PROFILE by id
 * with no tiers baked in. Passing those to the snapshot slot costs every
 * profile-backed row at zero, printing $0.00 for the 1h row the editor beside it
 * prices at $60.00. As `laborLines` they resolve against `laborProfiles`, which
 * is what the pricebook means by them.
 */
export type PricebookServiceCost = ServiceCost & { other: number };

export function serviceOwnCostBreakdown(
  item: PricebookComponentItemCacheItem | undefined,
  parts: ServiceOwnPart[],
  laborProfiles: LaborRateProfile[],
): PricebookServiceCost {
  const isDynamic = String(item?.priceStrategy ?? '').toUpperCase() === 'DYNAMIC';
  const source: ServiceCostSource = {
    laborLines: isDynamic ? item?.dynamicPricingConfig?.laborLines : item?.laborCostLines,
    estimatedHours: item?.estimatedHours,
    hourlyCost: item?.laborCostRate,
  };
  const composed = serviceCost(
    source,
    parts,
    laborProfiles,
    // What a margin share is taken from: the member's own price plus any part
    // it bills separately. The member's components carry that price.
    serviceCardRevenue(item?.price ?? item?.basePrice, parts),
  );
  const storedCost = Number(item?.cost ?? item?.costBasis ?? 0);
  const other = !isDynamic && Number.isFinite(storedCost) ? Math.max(0, storedCost) : 0;
  return { ...composed, other: round2(other), total: round2(composed.total + other) };
}

export function serviceOwnCost(
  item: PricebookComponentItemCacheItem | undefined,
  parts: ServiceOwnPart[],
  laborProfiles: LaborRateProfile[],
): number {
  return serviceOwnCostBreakdown(item, parts, laborProfiles).total;
}

/**
 * itemId → what that member SERVICE costs per unit, once its own parts have
 * been fetched. A service absent from the map has not resolved yet, which is
 * not the same as costing nothing.
 */
export type ServiceMemberCosts = Record<string, number>;
export type ServiceMemberCostBreakdowns = Record<string, PricebookServiceCost>;

/**
 * What ONE of this row costs us — the figure the Cost column states.
 *
 * A material or equipment row answers from its own cost column. A SERVICE row
 * cannot: see {@link serviceOwnCost}. It answers from the resolved map, and
 * `undefined` until that lands — a dash, which says "not known yet", rather
 * than a $0.00 that reads as free.
 */
export function componentUnitCost(
  item: PricebookComponentItemCacheItem | undefined,
  serviceMemberCosts?: ServiceMemberCosts,
): number | undefined {
  if (item?.type === 'service') return item.id ? serviceMemberCosts?.[item.id] : undefined;
  return getComponentItemCost(item);
}

/**
 * The service's pricing defaults, as the part rows resolve against them.
 *
 * A row that names no profile of its own falls back to the family default here,
 * and "use the materials profile for equipment too" decides which family an
 * equipment row belongs to.
 */
export interface ComponentMarkupContext {
  markupProfiles: MarkupProfile[];
  materialMarkupProfileId?: string | null;
  equipmentMarkupProfileId?: string | null;
  /** False means equipment has a profile of its own; anything else shares. */
  useSameMarkupProfile?: boolean;
  /**
   * False only while the profiles request is IN FLIGHT. Decides whether a
   * chosen profile that is not in the list is MISSING (deleted — keep the
   * item's own markup, as the server does) or simply not here yet (price at
   * cost for now). A request that has finished — with a list or with an error
   * — counts as loaded: the server's preview and apply take the missing-profile
   * fallback regardless, and holding the row at cost after a failed request
   * would show a price that save never persists. Callers read `isFetched`,
   * not `isSuccess`. Absent reads as loaded.
   */
  markupProfilesLoaded?: boolean;
  /** The item these parts hang under — see `componentSellingUnitPrice`. */
  parent?: { type?: string | null; priceStrategy?: string | null } | null;
}

/**
 * How the totals are asked to value a service's parts.
 *
 * The markup context answers "what is one part worth?". This adds the question
 * above it: does that figure add to what the customer pays, or is it only what
 * the work costs us?
 */
export interface PricebookComponentPricingContext extends ComponentMarkupContext {
  /**
   * A FLAT-price service sells for the one price the operator typed.
   *
   * Its included materials and equipment are what delivering that price COSTS,
   * not extras charged on top: a $2,000 flat service with $1,555 of parts sells
   * for $2,000, and the parts are the reason its margin is 22.25% rather than
   * 100%. Set this on the flat path and `bundleTotal` is the service's own
   * price; the cost figures are untouched, because cost is still cost.
   *
   * Time-and-materials is the other rule and leaves this off: there the labor
   * and the parts are precisely what sums to the price.
   */
  partsAreCostOnly?: boolean;
  /**
   * The parent is a BUNDLE, which sells for ITS OWN price and nothing else.
   *
   * Not a third flavour of `partsAreCostOnly`: a bundle's members are priced at
   * what they SELL for — that is what the bundle sums, and what "Included parts"
   * reports — they simply never charge a second time on top of the bundle's
   * price. Whichever way that price was arrived at, typed (`FLAT`) or summed
   * from the members (`COMPONENT_SUM`), it is the whole of what the customer
   * pays: operations' `previewBundle` returns `basePrice` or the members' sum,
   * never both, and gives every member `separatelyCharged: 0` because a bundle
   * is ONE customer-facing line.
   *
   * Without this a $1,131.81 bundle whose members are worth $1,131.81 reported a
   * bundle total of $2,263.62 — the contents charged once as the bundle and
   * again as themselves.
   */
  isBundle?: boolean;
  /**
   * What each member SERVICE costs, resolved from its labour and its own parts.
   *
   * Only a BUNDLE has service members, and only their own configuration can say
   * what they cost — the column the other rows answer from is 0 for them. Omit
   * it and a service member contributes no cost at all, which is a smaller lie
   * than the $0.00 it would otherwise print but still a lie; the surfaces that
   * headline a cost pass it.
   */
  serviceMemberCosts?: ServiceMemberCosts;
  /** The same service-member costs split into labor, materials, and equipment. */
  serviceMemberCostBreakdowns?: ServiceMemberCostBreakdowns;
}

/**
 * What one part of a service sells for per unit.
 *
 * The row's own rule decides. `COST_PLUS` — which is what the editor creates
 * every new part as — prices off the SUPPLIER COST and the markup that applies
 * to it, per the spec: `cost × (1 + markup)`. Only a row deliberately set to
 * catalog price takes the catalog's figure.
 *
 * This exists because the summary used to skip the question entirely and value
 * every part at its catalog price. A service whose parts were all "Cost only ·
 * 0% markup" — $1,800 equipment, $55 material — reported $3,261.60 of included
 * parts against $1,855.00 of cost, and then labelled the gap "Markup over cost
 * 75.8%": a markup the operator had explicitly declined, on a control that was
 * simultaneously reporting 0%. The rows and the arithmetic now read the same
 * fields, so the two cannot say different things.
 *
 * A part whose item reports no cost has nothing to mark up, so it keeps its
 * catalog price rather than silently becoming free.
 *
 * A SERVICE member is the exception, and needs no flag to detect: only a BUNDLE
 * can hold one, because a service cannot contain a service. A service already
 * carries the price it sells for, and that is what the bundle sums — operations
 * `previewBundle` prices a SERVICE member from its own `preview().calculatedPrice`
 * and never looks at the relationship's `pricingMethod` at all. Reading the row's
 * rule here instead valued such a member at its `costBasis`, which a dynamically
 * priced service is required to store as 0 — a service member worth thousands,
 * summed into the bundle as nothing.
 */
export function componentSellingUnitPrice(
  component: ServiceComponent,
  item: PricebookComponentItemCacheItem | undefined,
  context?: ComponentMarkupContext,
): number {
  const catalogPrice = getComponentItemPrice(item);
  const cost = getComponentItemCost(item);
  if (item?.type === 'service') return catalogPrice;
  if (component.pricingMethod !== 'COST_PLUS' || cost == null) return catalogPrice;
  // A bundle's explicit Cost-only rule is accounting-only: the supplier cost
  // belongs in cost and margin, but contributes no customer price. Services
  // use the same persisted 0% shape differently because their dynamic formula
  // is itself built from costs. The parent is therefore part of the meaning.
  if (
    String(context?.parent?.type ?? '').toLowerCase() === 'bundle' &&
    !component.markupProfileId &&
    component.customMarkupPercent === 0
  ) {
    return 0;
  }
  // A part INCLUDED in a flat-priced service is worth its cost, whatever
  // markup its row still carries: the service's price is the number the
  // operator typed, so no markup on an included part moves any total, and
  // the row's select says "Cost". A markup is real on a flat service only for
  // a part billed extra — its own customer line, priced from cost plus it.
  // Stated HERE, in the one function that produces the number, so a label
  // and a value can never disagree about it.
  const flatParent = Boolean(context?.parent && isFlatPricePricebookService(context.parent));
  if (flatParent && !componentBillsSeparately(component, item, context!.parent!)) {
    return cost;
  }

  // A flat service has no family default: its config is dropped on save and
  // the defaults control offers none. A default still sitting in form state
  // from the service's dynamic days must not price a billed-extra row the
  // editor would then show at "Cost + 20%" and the reload at cost — on a flat
  // service only the ROW's own profile or percentage is a markup.
  const familyProfileId = flatParent
    ? undefined
    : item?.type === 'equipment' && context?.useSameMarkupProfile === false
      ? context?.equipmentMarkupProfileId
      : context?.materialMarkupProfileId;
  const chosenProfileId = component.markupProfileId ?? familyProfileId;
  const profile = context?.markupProfiles.find((candidate) => candidate.id === chosenProfileId);
  // A profile that was chosen and cannot be found is not "Cost" — the operator
  // DID state a markup, and the server keeps the item's own in its place
  // (`resolveMarkupPercentForMissingProfile`). Read the same way here, or the
  // editor shows cost for a row preview/apply will persist at the item markup.
  // Only once the profiles have loaded, though: an empty list while they are
  // in flight is not a deleted profile — and a tenant whose last profile is
  // gone has a loaded, empty list, which IS. The caller says which.
  if (chosenProfileId && !profile && context?.markupProfilesLoaded !== false) {
    const percent = resolveMarkupPercent({
      markupOverride: component.customMarkupPercent,
      itemMarkupPercent: item?.markupPercent,
      itemBasePrice: getComponentItemPrice(item),
    });
    return cost * (1 + percent / 100);
  }
  const familyDefaultPercent = profile?.ranges.find(
    (range) => cost >= range.minCost && (range.maxCost == null || cost < range.maxCost),
  )?.percent;

  // The row's own percentage, then the profile band it sits in, then nothing —
  // and "nothing" means cost, which is exactly what "Cost" says. The item's
  // own catalog markup is a rung in ONE case only, lockstep with operations'
  // `resolveMarkupPercent`: a profile WAS chosen but none of its bands covers
  // this cost (legacy profiles predate the total-coverage rule). Selling such
  // a part at cost would silently ignore a profile the operator did choose;
  // with no profile chosen at all, cost is the choice.
  const percent = resolveMarkupPercent({
    markupOverride: component.customMarkupPercent,
    ...(profile && familyDefaultPercent == null
      ? { itemMarkupPercent: item?.markupPercent, itemBasePrice: getComponentItemPrice(item) }
      : {}),
    familyDefaultPercent,
  });
  return cost * (1 + percent / 100);
}

/**
 * Does this component bill the customer on a LINE OF ITS OWN?
 *
 * The "Included ⇄ Billed extra" control on the part row is this question, and
 * the answer is not re-derived here: `partPricingTreatmentForService` resolves
 * the treatment exactly as the estimate does when the service is materialized —
 * the relationship's own choice first, then the parent's pricing strategy — and
 * `chargesRevenue` turns that treatment into the money answer. Asking the same
 * two functions the document asks is what stops this panel from reporting a
 * composition the estimate then charges differently.
 *
 * Absent on a FLAT service means included: the flat price already covers the
 * part, which is the double-charging rule and what every component saved before
 * the control existed prices at. On the summing path the treatment stays null,
 * which reads as billed on its own row — as it always has.
 *
 * A BUNDLE rolls members up by default. A direct material/equipment relationship
 * may now explicitly say `separate_charge`; that is the only bundle case that
 * bills on its own line. Missing treatment remains rolled up for compatibility,
 * member services always remain inside the bundle, and their nested parts keep
 * following the service rather than inheriting the bundle control.
 *
 * Exported because the part-row editor asks the same question for its own
 * footer: two implementations is how a table came to total a member under
 * "Billed extra, on their own lines" while the summary beside it charged the
 * customer nothing extra for it.
 */
export function componentBillsSeparately(
  component: ServiceComponent,
  item: PricebookComponentItemCacheItem | undefined,
  parent: { type?: string | null; priceStrategy?: string | null },
): boolean {
  if (String(parent.type ?? '').toLowerCase() === 'bundle') {
    // Bundle members historically had no placement decision and remain rolled
    // up when the field is absent. Only a direct part explicitly marked by the
    // new control becomes its own customer line; member services stay inside
    // the bundle, as do their nested parts.
    return item?.type !== 'service' && component.pricingTreatment === 'separate_charge';
  }
  return chargesRevenue({
    // A service's components are materials and equipment only — the picker
    // offers nothing else outside a bundle — and an untyped item is read as
    // material here for the same reason the cost split below reads it that way.
    type: item?.type === 'equipment' ? 'EQUIPMENT' : 'MATERIAL',
    pricingTreatment: partPricingTreatmentForService(component, parent),
  });
}

export interface PricebookComponentTotals {
  /** Parts the customer is actually buying — optional ones excluded. */
  totalIncluded: number;
  /**
   * What those members sell for in the catalog, before a bundle chooses to
   * contribute a material/equipment member at cost instead.
   *
   * This is deliberately independent of `totalIncluded`: on a manually priced
   * bundle it is the useful comparison with the price the operator typed, and
   * on a summed bundle it explains the difference when one member contributes
   * its supplier cost rather than its selling price.
   */
  catalogValue: number;
  /** Optional add-ons: offered, not sold, and not part of any total below. */
  totalOptional: number;
  /** Cost of the included parts, or null when no part reports a cost. */
  totalCost: number | null;
  /**
   * `totalCost` split the way a document reports cost — material and equipment
   * are separate lines on the margin panel, and a service that shows one lumped
   * "parts" figure cannot be compared with the same service on an estimate.
   */
  materialCost: number;
  equipmentCost: number;
  /** Service-member cost (labor plus that service's own parts). */
  serviceCost: number;
  /** Service-member cost split the same way an estimate displays it. */
  serviceLaborCost: number;
  serviceMaterialCost: number;
  serviceEquipmentCost: number;
  /** Other cost saved on flat service members. */
  serviceOtherCost: number;
  /**
   * What the parts marked "Billed extra" charge, on their own lines.
   *
   * A billed-extra part is a customer-facing line of its own: it has revenue
   * AND cost, and counting one side without the other is what made a flat
   * service carrying a separately-billed AC unit report the unit's $3,600 of
   * cost against none of its $6,400 of revenue — a 27.84% margin on a job that
   * earns 68.35%.
   *
   * On the FLAT path this is what `bundleTotal` adds to the service price. On
   * the summing path these parts are already inside `totalIncluded`, so the
   * figure is reported rather than added again. Always 0 on a BUNDLE, which has
   * no line but its own — see {@link componentBillsSeparately}.
   */
  separatelyChargedAmount: number;
  /** Markup across the included PARTS only — never the bundle's. */
  aggregateMarkupPct: number | null;
  optionalCount: number;
  /** The service's own price, before any parts. */
  serviceAmount: number;
  /**
   * What the customer pays.
   *
   * Three rules, one per kind of parent:
   *
   *   TIME & MATERIALS  service + included parts. The labor and the parts are
   *                     precisely what builds the price.
   *   FLAT service      the typed price, plus whatever bills extra beside it.
   *                     The flat price already covers the INCLUDED parts.
   *   BUNDLE            the bundle's own price, and nothing else. Its members
   *                     are what is IN it, never a second charge on top.
   *
   * See {@link PricebookComponentPricingContext.partsAreCostOnly},
   * {@link PricebookComponentPricingContext.isBundle} and
   * {@link PricebookComponentTotals.separatelyChargedAmount}.
   */
  bundleTotal: number;
  /**
   * Do the included parts add to the price, or only to the cost?
   *
   * Surfaces read this rather than re-deriving the pricing strategy: a panel
   * that headlines `bundleTotal` must not also print a "service + parts" row
   * that contradicts it.
   */
  partsAreCostOnly: boolean;
  /**
   * Is the parent a BUNDLE? Reported for the same reason `partsAreCostOnly` is:
   * a panel naming these figures must call the price by the name of the thing
   * that carries it, and must not offer a total the rule above does not produce.
   */
  isBundle: boolean;
  /** Are there any parts at all? A plain service must not show a bundle. */
  hasParts: boolean;
  /**
   * Has every part's price been resolved?
   *
   * False while the item lookup is in flight, and false for good if it failed.
   * A surface whose HEADLINE figure is the bundle total must wait for this:
   * the totals below are computed from the parts that resolved, so showing
   * them early states a price that is missing whatever has not arrived.
   */
  allPartsResolved: boolean;
}

/**
 * Totals for a service's parts, plus what the bundle as a whole is worth.
 *
 * Components whose pricebook item has not been resolved yet are skipped: their
 * price is unknown, and counting them as $0 would print a total that quietly
 * changes once the fetch lands.
 *
 * `servicePrice` is the service's own listed price. Pass 0 for a time-and-
 * materials service — its listed figure is an hourly rate, and adding parts to
 * an hourly rate produces a number that means nothing.
 */
/**
 * What a FLAT-price service costs, in the shape the margin panel reads.
 *
 * A flat price prices the OUTCOME, so the hours never reach the PRICE — but the
 * work still takes them and they still cost money, which is the whole point of
 * recording them on a flat service. This returned zero labor unconditionally, so
 * an operator could enter "1h @ $20" in the labor section, watch the row compute
 * $20, and see Labor stay at $0.00 in the summary beside it.
 *
 * The labor rows are now taken rather than a pre-reduced amount, and reduced by
 * `serviceCost` — the one helper that answers what a service costs. Two callers
 * previously each reduced them for themselves, six identical lines apiece, and a
 * third caller could have passed nothing at all: both parameters defaulted to 0,
 * so "I do not have the labor handy" and "this service has no labor" were the
 * same call. Now they are not expressible.
 *
 * The parts keep coming from `totals`, which has already resolved each
 * component's item and split material from equipment. The parts are cost only on
 * this path: they cost, they do not sell.
 */
export function flatServiceCostBreakdown(
  totals: PricebookComponentTotals,
  laborLines: ServiceLaborLine[],
  laborRateProfiles: LaborRateProfile[],
  /** The service's own price, for a margin-share row to take its share from. */
  servicePrice: number,
): {
  labor: number;
  laborHours: number;
  laborFromMargin: number;
  material: number;
  equipment: number;
} {
  // The parts are already resolved in `totals`, so none are handed over and the
  // card's contribution goes in whole: the price plus the parts billed on their
  // own, less what the parts cost — the basis the detail page and a document
  // take the share from.
  const contribution =
    (Number(servicePrice) || 0) +
    totals.separatelyChargedAmount -
    totals.materialCost -
    totals.equipmentCost;
  const cost = serviceCost({ laborLines }, [], laborRateProfiles, contribution);
  return {
    labor: cost.labor,
    laborHours: cost.laborHours,
    laborFromMargin: cost.laborFromMargin,
    material: totals.materialCost,
    equipment: totals.equipmentCost,
  };
}

/**
 * What a bundle priced from its members sells for.
 *
 * `COMPONENT_SUM` — "Add up what is in the bundle" — means the price IS the
 * members' contributions, so it moves whenever they do: a member added or
 * removed, a quantity changed, a member flipped between its cost and its price.
 * The form used to leave the typed figure alone through all of that, and an
 * operator who changed the contents watched the price sit still.
 *
 * The same sum operations' `previewBundle` derives, term for term: every
 * SELECTED member at its own resolved unit price (a service at what it sells
 * for, a part cost-plus aware) times its quantity. An OPTIONAL member is
 * offered rather than sold and contributes nothing, which is what keeps
 * `totalIncluded` the right half of the totals to read.
 *
 * `null` while a member's catalog row is still in flight: the sum would be
 * missing whatever has not arrived, and writing that into the price field would
 * be quoting a bundle short.
 */
export function summedBundlePrice(totals: PricebookComponentTotals): number | null {
  if (!totals.allPartsResolved) return null;
  return round2(totals.totalIncluded);
}

export function computePricebookComponentTotals(
  children: ServiceComponent[],
  itemCache: PricebookComponentItemCache,
  servicePrice = 0,
  pricing?: PricebookComponentPricingContext,
): PricebookComponentTotals {
  interface PartLine extends ComponentGroupableLine {
    lineSubtotal: number;
  }

  const resolved = children.filter((child) => Boolean(itemCache[child.itemId]));

  // The one line the flat rule turns on. Everything below it — every cost, the
  // material/equipment split, the parts' own valuation — is deliberately
  // unchanged: what a part costs does not depend on how the service is priced.
  const partsAreCostOnly = Boolean(pricing?.partsAreCostOnly);
  const isBundle = Boolean(pricing?.isBundle);
  // The parent, in the shape every treatment rule reads it — so the components
  // are charged here exactly as the editor labels them and the document bills
  // them. A bundle short-circuits the whole question; see the helper.
  const parent = {
    type: isBundle ? 'bundle' : 'service',
    priceStrategy: partsAreCostOnly ? 'FLAT' : 'DYNAMIC',
  };
  // ONE valuation per part, used for the bundle figures and the totals alike,
  // with the parent in hand — an included part of a flat service is worth its
  // cost whatever its row carries, and both passes have to say so.
  const pricingWithParent: ComponentMarkupContext = {
    ...pricing,
    markupProfiles: pricing?.markupProfiles ?? [],
    parent,
  };
  const sellingAmountOf = (child: ServiceComponent) =>
    componentSellingUnitPrice(child, itemCache[child.itemId], pricingWithParent) * child.quantity;

  const group: ComponentGroup<PartLine> = {
    parent: { lineKey: 'service', lineSubtotal: Number(servicePrice) || 0 },
    components: resolved.map((child) => ({
      parentLineKey: 'service',
      lineSubtotal: sellingAmountOf(child),
      // Optional in the pricebook = deselected on the estimate.
      isSelected: !child.isOptional,
    })),
    isRolledUp: true,
  };

  const economics = bundleEconomics(group);

  let totalCost = 0;
  let totalCostBase = 0;
  let catalogValue = 0;
  let materialCost = 0;
  let equipmentCost = 0;
  let serviceCost = 0;
  let serviceLaborCost = 0;
  let serviceMaterialCost = 0;
  let serviceEquipmentCost = 0;
  let serviceOtherCost = 0;
  let optionalCount = 0;
  let separatelyChargedAmount = 0;
  for (const child of resolved) {
    if (child.isOptional) {
      optionalCount += 1;
      continue;
    }
    const item = itemCache[child.itemId];
    const sellingAmount = sellingAmountOf(child);
    catalogValue += getComponentItemPrice(item) * child.quantity;
    // BOTH sides of a billed-extra part, or neither. Its cost lands below like
    // every other part's; this is the revenue that goes with it.
    if (componentBillsSeparately(child, item, parent)) separatelyChargedAmount += sellingAmount;
    const cost = componentUnitCost(item, pricing?.serviceMemberCosts);
    if (cost != null) {
      const amount = cost * child.quantity;
      totalCost += amount;
      // The SAME figure the parts subtotal is built from, so "markup over cost"
      // can never state a percentage the subtotal contradicts.
      totalCostBase += sellingAmount;
      // The material/equipment split is what a document reports for PARTS, and
      // a member SERVICE is neither. Its cost is already labour plus its own
      // parts, so filing it under materials would put hours in the materials
      // line of a margin panel. It counts in `totalCost` and stops there.
      if (item?.type === 'equipment') equipmentCost += amount;
      else if (item?.type === 'service') {
        serviceCost += amount;
        const breakdown = pricing?.serviceMemberCostBreakdowns?.[child.itemId];
        if (breakdown) {
          serviceLaborCost += breakdown.labor * child.quantity;
          serviceMaterialCost += breakdown.material * child.quantity;
          serviceEquipmentCost += breakdown.equipment * child.quantity;
          serviceOtherCost += breakdown.other * child.quantity;
        }
      }
      else materialCost += amount;
    }
  }

  const aggregateMarkupPct =
    totalCost > 0 && totalCostBase > 0 ? ((totalCostBase - totalCost) / totalCost) * 100 : null;

  const billedExtra = round2(separatelyChargedAmount);

  return {
    totalIncluded: economics.partsAmount,
    catalogValue: round2(catalogValue),
    totalOptional: economics.unselectedPartsAmount,
    totalCost: totalCost > 0 ? totalCost : null,
    materialCost,
    equipmentCost,
    serviceCost,
    serviceLaborCost,
    serviceMaterialCost,
    serviceEquipmentCost,
    serviceOtherCost,
    separatelyChargedAmount: billedExtra,
    aggregateMarkupPct,
    optionalCount,
    serviceAmount: economics.serviceAmount,
    // A BUNDLE's price is the whole of what it charges — its members are what is
    // inside it, and `billedExtra` is 0 for them by construction. The flat
    // service's price covers the INCLUDED parts and nothing else, so a part
    // billed extra is its own line beside it and adds on top.
    bundleTotal: isBundle
      ? economics.serviceAmount
      : partsAreCostOnly
        ? economics.serviceAmount + billedExtra
        : economics.bundleTotal,
    partsAreCostOnly,
    isBundle,
    hasParts: children.length > 0,
    allPartsResolved: resolved.length === children.length,
  };
}
